import React, { useCallback, useRef, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import TextAlign from '@tiptap/extension-text-align';
import { useTheme } from '../contexts/ThemeContext';
import Underline from '@tiptap/extension-underline';
import Placeholder from '@tiptap/extension-placeholder';
import { Color } from '@tiptap/extension-color';
import { TextStyle } from '@tiptap/extension-text-style';
import Highlight from '@tiptap/extension-highlight';
import FontFamily from '@tiptap/extension-font-family';
import Subscript from '@tiptap/extension-subscript';
import Superscript from '@tiptap/extension-superscript';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import Youtube from '@tiptap/extension-youtube';
import CharacterCount from '@tiptap/extension-character-count';
import { 
  Bold, Italic, Underline as UnderlineIcon, Strikethrough,
  Heading1, Heading2, Heading3,
  List, ListOrdered, CheckSquare,
  AlignLeft, AlignCenter, AlignRight, AlignJustify,
  Link as LinkIcon, Image as ImageIcon,
  Table as TableIcon, Undo, Redo,
  Quote, Code, Minus, Palette, Highlighter,
  Type, Subscript as SubIcon, Superscript as SuperIcon,
  Youtube as YoutubeIcon, Columns, ChevronDown,
  Plus, Trash2, RowsIcon, ColumnsIcon,
  Video, FileCode, Upload
} from 'lucide-react';
import { sanitizeHTML } from '../lib/sanitize';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Predefined colors for text and highlight
const TEXT_COLORS = [
  { name: 'Default', color: null },
  { name: 'Brand Green', color: '#A2FF00' },
  { name: 'White', color: '#FFFFFF' },
  { name: 'Gray', color: '#9CA3AF' },
  { name: 'Red', color: '#EF4444' },
  { name: 'Orange', color: '#F97316' },
  { name: 'Yellow', color: '#EAB308' },
  { name: 'Green', color: '#22C55E' },
  { name: 'Blue', color: '#3B82F6' },
  { name: 'Purple', color: '#A855F7' },
  { name: 'Pink', color: '#EC4899' },
];

const HIGHLIGHT_COLORS = [
  { name: 'None', color: null },
  { name: 'Yellow', color: '#FEF08A' },
  { name: 'Green', color: '#BBF7D0' },
  { name: 'Blue', color: '#BFDBFE' },
  { name: 'Purple', color: '#E9D5FF' },
  { name: 'Pink', color: '#FBCFE8' },
  { name: 'Orange', color: '#FED7AA' },
  { name: 'Red', color: '#FECACA' },
  { name: 'Gray', color: '#E5E7EB' },
];

const FONT_FAMILIES = [
  { name: 'Default', value: null },
  { name: 'Play', value: 'Play, sans-serif' },
  { name: 'Sans Serif', value: 'Inter, sans-serif' },
  { name: 'Serif', value: 'Georgia, serif' },
  { name: 'Mono', value: 'monospace' },
  { name: 'Unbounded', value: 'Unbounded, sans-serif' },
];

const FONT_SIZES = [
  { name: 'Small', value: '12px', size: '12' },
  { name: 'Normal', value: '16px', size: '16' },
  { name: 'Medium', value: '18px', size: '18' },
  { name: 'Large', value: '20px', size: '20' },
  { name: 'X-Large', value: '24px', size: '24' },
  { name: 'XX-Large', value: '32px', size: '32' },
  { name: 'Huge', value: '48px', size: '48' },
];

// Menu Button Component
const MenuButton = ({ onClick, active, disabled, children, title }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    title={title}
    className={`p-1.5 rounded transition-colors ${
      active 
        ? 'bg-[#A2FF00] text-black' 
        : disabled
          ? 'text-gray-600 cursor-not-allowed'
          : 'text-gray-400 hover:text-white hover:bg-white/10'
    }`}
  >
    {children}
  </button>
);

// Dropdown Menu Component
const DropdownMenu = ({ trigger, children, isOpen, onToggle }) => {
  return (
    <div className="relative">
      <button
        type="button"
        onClick={onToggle}
        className="flex items-center gap-1 px-2 py-1.5 rounded text-gray-400 hover:text-white hover:bg-white/10 transition-colors"
      >
        {trigger}
        <ChevronDown className="w-3 h-3" />
      </button>
      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={onToggle} />
          <div className="absolute top-full left-0 mt-1 bg-[#1A1A1A] border border-white/20 rounded-sm shadow-xl z-50 min-w-[150px]">
            {children}
          </div>
        </>
      )}
    </div>
  );
};

// Color Picker Component
const ColorPicker = ({ colors, activeColor, onSelect, title }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <DropdownMenu
      trigger={
        <div className="flex items-center gap-1">
          <div 
            className="w-4 h-4 rounded border border-white/30"
            style={{ backgroundColor: activeColor || 'transparent' }}
          />
        </div>
      }
      isOpen={isOpen}
      onToggle={() => setIsOpen(!isOpen)}
    >
      <div className="p-2">
        <div className="text-xs text-gray-500 mb-2">{title}</div>
        <div className="grid grid-cols-4 gap-1">
          {colors.map((c) => (
            <button
              key={c.name}
              onClick={() => { onSelect(c.color); setIsOpen(false); }}
              className={`w-6 h-6 rounded border transition-transform hover:scale-110 ${
                activeColor === c.color ? 'border-[#A2FF00] ring-1 ring-[#A2FF00]' : 'border-white/20'
              }`}
              style={{ backgroundColor: c.color || 'transparent' }}
              title={c.name}
            >
              {!c.color && <span className="text-gray-500 text-xs">∅</span>}
            </button>
          ))}
        </div>
      </div>
    </DropdownMenu>
  );
};

const MenuDivider = () => (
  <div className="w-px h-6 bg-white/20 mx-1" />
);

// Custom Column Extension (using div wrapper)
const ColumnLayout = ({ editor, columns = 2 }) => {
  const insertColumns = () => {
    const columnWidth = Math.floor(100 / columns);
    let html = '<div class="column-layout" style="display: grid; grid-template-columns: repeat(' + columns + ', 1fr); gap: 1rem;">';
    for (let i = 0; i < columns; i++) {
      html += '<div class="column" style="border: 1px dashed rgba(255,255,255,0.2); padding: 1rem; min-height: 100px;"><p>Column ' + (i + 1) + '</p></div>';
    }
    html += '</div>';
    editor.chain().focus().insertContent(html).run();
  };
  
  return (
    <button
      type="button"
      onClick={insertColumns}
      title={`Insert ${columns} Columns`}
      className="p-1.5 rounded text-gray-400 hover:text-white hover:bg-white/10 transition-colors"
    >
      <Columns className="w-4 h-4" />
    </button>
  );
};

const RichTextEditor = ({ content, onChange, placeholder = "Start typing...", editorKey = '', showCharCount = true }) => {
  const { isDark } = useTheme();
  const fileInputRef = useRef(null);
  const htmlFileInputRef = useRef(null);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [videoUrl, setVideoUrl] = useState('');
  const [showHtmlPreview, setShowHtmlPreview] = useState(false);
  const [htmlPreviewContent, setHtmlPreviewContent] = useState('');

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
        // StarterKit includes basic extensions, we keep them all
      }),
      Underline,
      TextStyle,
      Color,
      Highlight.configure({
        multicolor: true,
      }),
      FontFamily.configure({
        types: ['textStyle'],
      }),
      Subscript,
      Superscript,
      TaskList,
      TaskItem.configure({
        nested: true,
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-[#A2FF00] underline',
        },
      }),
      Image.configure({
        HTMLAttributes: {
          class: 'max-w-full h-auto rounded',
        },
      }),
      Youtube.configure({
        width: 640,
        height: 360,
        HTMLAttributes: {
          class: 'w-full aspect-video rounded',
        },
      }),
      Table.configure({
        resizable: true,
        HTMLAttributes: {
          class: 'border-collapse border border-white/20',
        },
      }),
      TableRow,
      TableHeader.configure({
        HTMLAttributes: {
          class: 'bg-[#111] border border-white/20 p-2 text-left font-bold text-[#A2FF00]',
        },
      }),
      TableCell.configure({
        HTMLAttributes: {
          class: 'border border-white/20 p-2',
        },
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Placeholder.configure({
        placeholder,
      }),
      CharacterCount.configure({
        limit: null,
      }),
    ],
    content: content || '',
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: 'prose prose-invert max-w-none focus:outline-none min-h-[300px] p-4',
      },
    },
  }, [editorKey]);

  const setLink = useCallback(() => {
    if (!editor) return;
    
    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('Enter URL:', previousUrl);

    if (url === null) return;

    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }

    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  }, [editor]);

  const addImageFromUrl = useCallback(() => {
    if (!editor) return;
    
    const url = window.prompt('Enter image URL:');
    
    if (url && url.trim()) {
      editor.chain().focus().setImage({ src: url.trim() }).run();
    }
  }, [editor]);

  const triggerImageUpload = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !editor) return;

    // Check file size (1MB limit)
    const MAX_SIZE = 1 * 1024 * 1024; // 1MB
    if (file.size > MAX_SIZE) {
      alert(`Image is too large. Maximum size is 1MB. Your file is ${(file.size / (1024 * 1024)).toFixed(2)}MB`);
      e.target.value = '';
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch(`${BACKEND_URL}/api/uploads`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });

      if (response.ok) {
        const data = await response.json();
        // Use absolute URL with backend URL so images work across all environments
        // Convert /api/uploads/filename to full URL with /api/public/uploads/filename
        const publicPath = data.url.replace('/api/uploads/', '/api/public/uploads/');
        const absoluteUrl = `${BACKEND_URL}${publicPath}`;
        editor.chain().focus().setImage({ src: absoluteUrl }).run();
      } else {
        const error = await response.json().catch(() => ({ detail: 'Upload failed' }));
        alert(error.detail || 'Failed to upload image');
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert('Failed to upload image. Please check your connection and try again.');
    }

    e.target.value = '';
  };

  const insertTable = useCallback(() => {
    if (!editor) return;
    editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
  }, [editor]);

  const addYoutubeVideo = useCallback(() => {
    if (!editor) return;
    const url = window.prompt('Enter YouTube URL:');
    if (url) {
      editor.commands.setYoutubeVideo({ src: url });
    }
  }, [editor]);

  const insertColumns = useCallback((cols) => {
    if (!editor) return;
    let html = `<div class="column-layout" style="display: grid; grid-template-columns: repeat(${cols}, 1fr); gap: 1rem; margin: 1rem 0;">`;
    for (let i = 0; i < cols; i++) {
      html += `<div class="column" style="border: 1px dashed rgba(255,255,255,0.2); padding: 1rem; min-height: 100px; border-radius: 4px;"><p>Column ${i + 1}</p></div>`;
    }
    html += '</div>';
    editor.chain().focus().insertContent(html).run();
    setOpenDropdown(null);
  }, [editor]);

  // Insert generic video embed (Vimeo, Loom, etc.)
  const insertVideoEmbed = useCallback(() => {
    if (!editor || !videoUrl) return;
    
    // Check if it's a YouTube URL - use native YouTube extension
    if (videoUrl.includes('youtube.com') || videoUrl.includes('youtu.be')) {
      editor.commands.setYoutubeVideo({ src: videoUrl });
      setVideoUrl('');
      setShowVideoModal(false);
      return;
    }
    
    // For Vimeo, Loom, or other video URLs
    let embedUrl = videoUrl;
    
    // Convert Vimeo URLs to embed format
    if (videoUrl.includes('vimeo.com')) {
      const vimeoId = videoUrl.match(/vimeo\.com\/(\d+)/);
      if (vimeoId) {
        embedUrl = `https://player.vimeo.com/video/${vimeoId[1]}`;
      }
    }
    
    // Convert Loom URLs to embed format
    if (videoUrl.includes('loom.com/share')) {
      embedUrl = videoUrl.replace('/share/', '/embed/');
    }
    
    // Insert iframe for generic video embed
    const html = `<div class="video-embed" style="position: relative; padding-bottom: 56.25%; height: 0; overflow: hidden; margin: 1rem 0;">
      <iframe 
        src="${embedUrl}" 
        style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; border: none; border-radius: 8px;"
        allow="autoplay; fullscreen; picture-in-picture"
        allowfullscreen
      ></iframe>
    </div>`;
    
    editor.chain().focus().insertContent(html).run();
    setVideoUrl('');
    setShowVideoModal(false);
  }, [editor, videoUrl]);

  // Handle HTML file upload
  const handleHtmlUpload = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file || !editor) return;
    
    const reader = new FileReader();
    reader.onload = (event) => {
      const htmlContent = event.target?.result;
      if (htmlContent) {
        setHtmlPreviewContent(htmlContent);
        setShowHtmlPreview(true);
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  }, [editor]);

  // Insert HTML content into editor
  const insertHtmlContent = useCallback(() => {
    if (!editor || !htmlPreviewContent) return;
    
    // Wrap in a container div for proper styling
    const wrappedHtml = `<div class="html-template-block" style="margin: 1rem 0; padding: 1rem; border: 1px solid rgba(255,255,255,0.1); border-radius: 8px; background: rgba(0,0,0,0.3);">${htmlPreviewContent}</div>`;
    
    editor.chain().focus().insertContent(wrappedHtml).run();
    setHtmlPreviewContent('');
    setShowHtmlPreview(false);
  }, [editor, htmlPreviewContent]);

  const toggleDropdown = (name) => {
    setOpenDropdown(openDropdown === name ? null : name);
  };

  if (!editor) {
    return (
      <div className="bg-[#0A0A0A] border border-white/20 rounded-sm p-4">
        <div className="text-gray-500">Loading editor...</div>
      </div>
    );
  }

  const currentColor = editor.getAttributes('textStyle').color;
  const currentHighlight = editor.getAttributes('highlight').color;
  const currentFont = editor.getAttributes('textStyle').fontFamily;

  return (
    <div className={`${isDark ? 'bg-[#0A0A0A] border-white/20' : 'bg-white border-gray-200'} border rounded-sm overflow-hidden`}>
      {/* Toolbar Row 1 */}
      <div className={`${isDark ? 'bg-[#111] border-white/20' : 'bg-gray-50 border-gray-200'} border-b p-1.5 flex flex-wrap items-center gap-0.5`}>
        {/* Undo/Redo */}
        <MenuButton
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().undo()}
          title="Undo (Ctrl+Z)"
        >
          <Undo className="w-4 h-4" />
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().redo()}
          title="Redo (Ctrl+Y)"
        >
          <Redo className="w-4 h-4" />
        </MenuButton>

        <MenuDivider />

        {/* Font Family Dropdown */}
        <DropdownMenu
          trigger={<Type className="w-4 h-4" />}
          isOpen={openDropdown === 'font'}
          onToggle={() => toggleDropdown('font')}
        >
          <div className="py-1">
            {FONT_FAMILIES.map((font) => (
              <button
                key={font.name}
                onClick={() => {
                  if (font.value) {
                    editor.chain().focus().setFontFamily(font.value).run();
                  } else {
                    editor.chain().focus().unsetFontFamily().run();
                  }
                  setOpenDropdown(null);
                }}
                className={`w-full px-3 py-1.5 text-left text-sm hover:bg-white/10 ${
                  currentFont === font.value ? 'text-[#A2FF00]' : 'text-gray-300'
                }`}
                style={{ fontFamily: font.value || 'inherit' }}
              >
                {font.name}
              </button>
            ))}
          </div>
        </DropdownMenu>

        {/* Font Size Dropdown */}
        <DropdownMenu
          trigger={<span className="text-xs">Size</span>}
          isOpen={openDropdown === 'size'}
          onToggle={() => toggleDropdown('size')}
        >
          <div className="py-1">
            {FONT_SIZES.map((size) => (
              <button
                key={size.name}
                onClick={() => {
                  editor.chain().focus().setMark('textStyle', { fontSize: size.value }).run();
                  setOpenDropdown(null);
                }}
                className="w-full px-3 py-1.5 text-left text-sm text-gray-300 hover:bg-white/10 flex items-center justify-between"
              >
                <span style={{ fontSize: Math.min(parseInt(size.size), 20) + 'px' }}>{size.name}</span>
                <span className="text-gray-500 text-xs ml-2">{size.size}px</span>
              </button>
            ))}
          </div>
        </DropdownMenu>

        <MenuDivider />

        {/* Text Formatting */}
        <MenuButton
          onClick={() => editor.chain().focus().toggleBold().run()}
          active={editor.isActive('bold')}
          title="Bold (Ctrl+B)"
        >
          <Bold className="w-4 h-4" />
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().toggleItalic().run()}
          active={editor.isActive('italic')}
          title="Italic (Ctrl+I)"
        >
          <Italic className="w-4 h-4" />
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          active={editor.isActive('underline')}
          title="Underline (Ctrl+U)"
        >
          <UnderlineIcon className="w-4 h-4" />
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().toggleStrike().run()}
          active={editor.isActive('strike')}
          title="Strikethrough"
        >
          <Strikethrough className="w-4 h-4" />
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().toggleSubscript().run()}
          active={editor.isActive('subscript')}
          title="Subscript"
        >
          <SubIcon className="w-4 h-4" />
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().toggleSuperscript().run()}
          active={editor.isActive('superscript')}
          title="Superscript"
        >
          <SuperIcon className="w-4 h-4" />
        </MenuButton>

        <MenuDivider />

        {/* Text Color */}
        <div className="relative">
          <button
            onClick={() => toggleDropdown('textColor')}
            className="flex items-center gap-1 p-1.5 rounded text-gray-400 hover:text-white hover:bg-white/10"
            title="Text Color"
          >
            <Palette className="w-4 h-4" />
            <div 
              className="w-3 h-1 rounded-full"
              style={{ backgroundColor: currentColor || '#9CA3AF' }}
            />
          </button>
          {openDropdown === 'textColor' && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setOpenDropdown(null)} />
              <div className="absolute top-full left-0 mt-1 bg-[#1A1A1A] border border-white/20 rounded-sm shadow-xl z-50 p-2">
                <div className="text-xs text-gray-500 mb-2">Text Color</div>
                <div className="grid grid-cols-4 gap-1">
                  {TEXT_COLORS.map((c) => (
                    <button
                      key={c.name}
                      onClick={() => {
                        if (c.color) {
                          editor.chain().focus().setColor(c.color).run();
                        } else {
                          editor.chain().focus().unsetColor().run();
                        }
                        setOpenDropdown(null);
                      }}
                      className={`w-6 h-6 rounded border transition-transform hover:scale-110 ${
                        currentColor === c.color ? 'border-[#A2FF00] ring-1 ring-[#A2FF00]' : 'border-white/20'
                      }`}
                      style={{ backgroundColor: c.color || '#1A1A1A' }}
                      title={c.name}
                    >
                      {!c.color && <span className="text-gray-500 text-xs">A</span>}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Highlight Color */}
        <div className="relative">
          <button
            onClick={() => toggleDropdown('highlight')}
            className="flex items-center gap-1 p-1.5 rounded text-gray-400 hover:text-white hover:bg-white/10"
            title="Highlight"
          >
            <Highlighter className="w-4 h-4" />
            <div 
              className="w-3 h-1 rounded-full"
              style={{ backgroundColor: currentHighlight || 'transparent' }}
            />
          </button>
          {openDropdown === 'highlight' && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setOpenDropdown(null)} />
              <div className="absolute top-full left-0 mt-1 bg-[#1A1A1A] border border-white/20 rounded-sm shadow-xl z-50 p-2">
                <div className="text-xs text-gray-500 mb-2">Highlight</div>
                <div className="grid grid-cols-3 gap-1">
                  {HIGHLIGHT_COLORS.map((c) => (
                    <button
                      key={c.name}
                      onClick={() => {
                        if (c.color) {
                          editor.chain().focus().toggleHighlight({ color: c.color }).run();
                        } else {
                          editor.chain().focus().unsetHighlight().run();
                        }
                        setOpenDropdown(null);
                      }}
                      className={`w-6 h-6 rounded border transition-transform hover:scale-110 ${
                        currentHighlight === c.color ? 'border-[#A2FF00] ring-1 ring-[#A2FF00]' : 'border-white/20'
                      }`}
                      style={{ backgroundColor: c.color || '#1A1A1A' }}
                      title={c.name}
                    >
                      {!c.color && <span className="text-gray-500 text-xs">∅</span>}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>

        <MenuDivider />

        {/* Headings */}
        <MenuButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          active={editor.isActive('heading', { level: 1 })}
          title="Heading 1"
        >
          <Heading1 className="w-4 h-4" />
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          active={editor.isActive('heading', { level: 2 })}
          title="Heading 2"
        >
          <Heading2 className="w-4 h-4" />
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          active={editor.isActive('heading', { level: 3 })}
          title="Heading 3"
        >
          <Heading3 className="w-4 h-4" />
        </MenuButton>

        <MenuDivider />

        {/* Lists */}
        <MenuButton
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          active={editor.isActive('bulletList')}
          title="Bullet List"
        >
          <List className="w-4 h-4" />
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          active={editor.isActive('orderedList')}
          title="Numbered List"
        >
          <ListOrdered className="w-4 h-4" />
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().toggleTaskList().run()}
          active={editor.isActive('taskList')}
          title="Task List"
        >
          <CheckSquare className="w-4 h-4" />
        </MenuButton>

        <MenuDivider />

        {/* Alignment */}
        <MenuButton
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
          active={editor.isActive({ textAlign: 'left' })}
          title="Align Left"
        >
          <AlignLeft className="w-4 h-4" />
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().setTextAlign('center').run()}
          active={editor.isActive({ textAlign: 'center' })}
          title="Align Center"
        >
          <AlignCenter className="w-4 h-4" />
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().setTextAlign('right').run()}
          active={editor.isActive({ textAlign: 'right' })}
          title="Align Right"
        >
          <AlignRight className="w-4 h-4" />
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().setTextAlign('justify').run()}
          active={editor.isActive({ textAlign: 'justify' })}
          title="Justify"
        >
          <AlignJustify className="w-4 h-4" />
        </MenuButton>
      </div>

      {/* Toolbar Row 2 */}
      <div className={`${isDark ? 'bg-[#111] border-white/20' : 'bg-gray-50 border-gray-200'} border-b p-1.5 flex flex-wrap items-center gap-0.5`}>
        {/* Block Elements */}
        <MenuButton
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          active={editor.isActive('blockquote')}
          title="Quote"
        >
          <Quote className="w-4 h-4" />
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().toggleCodeBlock().run()}
          active={editor.isActive('codeBlock')}
          title="Code Block"
        >
          <Code className="w-4 h-4" />
        </MenuButton>
        <MenuButton
          onClick={() => editor.chain().focus().setHorizontalRule().run()}
          title="Horizontal Rule"
        >
          <Minus className="w-4 h-4" />
        </MenuButton>

        <MenuDivider />

        {/* Insert */}
        <MenuButton
          onClick={setLink}
          active={editor.isActive('link')}
          title="Add Link"
        >
          <LinkIcon className="w-4 h-4" />
        </MenuButton>
        
        {/* Image Dropdown */}
        <DropdownMenu
          trigger={<ImageIcon className="w-4 h-4" />}
          isOpen={openDropdown === 'image'}
          onToggle={() => toggleDropdown('image')}
        >
          <div className="py-1">
            <button
              onClick={() => {
                triggerImageUpload();
                setOpenDropdown(null);
              }}
              className="w-full px-3 py-1.5 text-left text-sm text-gray-300 hover:bg-white/10 flex items-center gap-2"
            >
              <Upload className="w-3 h-3" />
              Upload Image (Max 1MB)
            </button>
            <button
              onClick={() => {
                addImageFromUrl();
                setOpenDropdown(null);
              }}
              className="w-full px-3 py-1.5 text-left text-sm text-gray-300 hover:bg-white/10 flex items-center gap-2"
            >
              <LinkIcon className="w-3 h-3" />
              Insert from URL
            </button>
          </div>
        </DropdownMenu>
        
        <MenuButton
          onClick={addYoutubeVideo}
          title="Add YouTube Video"
        >
          <YoutubeIcon className="w-4 h-4" />
        </MenuButton>
        <MenuButton
          onClick={() => setShowVideoModal(true)}
          title="Embed Video (Vimeo, Loom, etc.)"
        >
          <Video className="w-4 h-4" />
        </MenuButton>
        <MenuButton
          onClick={() => htmlFileInputRef.current?.click()}
          title="Upload HTML Template"
        >
          <FileCode className="w-4 h-4" />
        </MenuButton>

        <MenuDivider />

        {/* Table Controls */}
        <MenuButton
          onClick={insertTable}
          title="Insert Table"
        >
          <TableIcon className="w-4 h-4" />
        </MenuButton>
        
        {editor.isActive('table') && (
          <>
            <MenuButton
              onClick={() => editor.chain().focus().addColumnAfter().run()}
              title="Add Column"
            >
              <Plus className="w-3 h-3" />
              <ColumnsIcon className="w-3 h-3" />
            </MenuButton>
            <MenuButton
              onClick={() => editor.chain().focus().addRowAfter().run()}
              title="Add Row"
            >
              <Plus className="w-3 h-3" />
              <RowsIcon className="w-3 h-3" />
            </MenuButton>
            <MenuButton
              onClick={() => editor.chain().focus().deleteColumn().run()}
              title="Delete Column"
            >
              <Trash2 className="w-3 h-3 text-red-400" />
            </MenuButton>
            <MenuButton
              onClick={() => editor.chain().focus().deleteRow().run()}
              title="Delete Row"
            >
              <Trash2 className="w-3 h-3 text-red-400" />
            </MenuButton>
            <MenuButton
              onClick={() => editor.chain().focus().deleteTable().run()}
              title="Delete Table"
            >
              <Trash2 className="w-4 h-4 text-red-400" />
            </MenuButton>
          </>
        )}

        <MenuDivider />

        {/* Columns */}
        <DropdownMenu
          trigger={<Columns className="w-4 h-4" />}
          isOpen={openDropdown === 'columns'}
          onToggle={() => toggleDropdown('columns')}
        >
          <div className="py-1">
            <button
              onClick={() => insertColumns(2)}
              className="w-full px-3 py-1.5 text-left text-sm text-gray-300 hover:bg-white/10 flex items-center gap-2"
            >
              <div className="flex gap-0.5">
                <div className="w-3 h-4 bg-gray-600 rounded-sm" />
                <div className="w-3 h-4 bg-gray-600 rounded-sm" />
              </div>
              2 Columns
            </button>
            <button
              onClick={() => insertColumns(3)}
              className="w-full px-3 py-1.5 text-left text-sm text-gray-300 hover:bg-white/10 flex items-center gap-2"
            >
              <div className="flex gap-0.5">
                <div className="w-2 h-4 bg-gray-600 rounded-sm" />
                <div className="w-2 h-4 bg-gray-600 rounded-sm" />
                <div className="w-2 h-4 bg-gray-600 rounded-sm" />
              </div>
              3 Columns
            </button>
            <button
              onClick={() => insertColumns(4)}
              className="w-full px-3 py-1.5 text-left text-sm text-gray-300 hover:bg-white/10 flex items-center gap-2"
            >
              <div className="flex gap-0.5">
                <div className="w-1.5 h-4 bg-gray-600 rounded-sm" />
                <div className="w-1.5 h-4 bg-gray-600 rounded-sm" />
                <div className="w-1.5 h-4 bg-gray-600 rounded-sm" />
                <div className="w-1.5 h-4 bg-gray-600 rounded-sm" />
              </div>
              4 Columns
            </button>
          </div>
        </DropdownMenu>

        {/* Hidden file input for image upload */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleImageUpload}
          className="hidden"
        />

        {/* Hidden file input for HTML upload */}
        <input
          ref={htmlFileInputRef}
          type="file"
          accept=".html,.htm"
          onChange={handleHtmlUpload}
          className="hidden"
        />

        {/* Character Count */}
        {showCharCount && (
          <div className="ml-auto text-xs text-gray-500">
            {editor.storage.characterCount.characters()} characters · {editor.storage.characterCount.words()} words
          </div>
        )}
      </div>

      {/* Video Embed Modal */}
      {showVideoModal && (
        <div className={`fixed inset-0 ${isDark ? 'bg-black/70' : 'bg-black/50'} flex items-center justify-center z-50`} onClick={() => setShowVideoModal(false)}>
          <div className={`${isDark ? 'bg-[#1A1A1A] border-white/20' : 'bg-white border-gray-200'} border rounded-sm p-6 w-full max-w-md`} onClick={e => e.stopPropagation()}>
            <h3 className={`${isDark ? 'text-white' : 'text-gray-900'} font-bold mb-4 flex items-center gap-2`}>
              <Video className={`w-5 h-5 ${isDark ? 'text-[#A2FF00]' : 'text-green-500'}`} />
              Embed Video
            </h3>
            <p className={`${isDark ? 'text-gray-400' : 'text-gray-600'} text-sm mb-4`}>
              Paste a video URL from YouTube, Vimeo, Loom, or any embeddable video source.
            </p>
            <input
              type="text"
              value={videoUrl}
              onChange={(e) => setVideoUrl(e.target.value)}
              placeholder="https://www.youtube.com/watch?v=..."
              className={`w-full px-4 py-2 ${isDark ? 'bg-black border-white/20 text-white' : 'bg-gray-50 border-gray-300 text-gray-900'} border rounded-sm placeholder-gray-500 focus:border-[#A2FF00] outline-none mb-4`}
              autoFocus
            />
            <div className="flex justify-end gap-3">
              <button
                onClick={() => { setShowVideoModal(false); setVideoUrl(''); }}
                className={`px-4 py-2 ${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'} transition-colors`}
              >
                Cancel
              </button>
              <button
                onClick={insertVideoEmbed}
                disabled={!videoUrl}
                className={`px-4 py-2 ${isDark ? 'bg-[#A2FF00] hover:bg-[#8FE600]' : 'bg-green-500 hover:bg-green-600'} text-black font-bold rounded-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                Embed Video
              </button>
            </div>
          </div>
        </div>
      )}

      {/* HTML Preview Modal */}
      {showHtmlPreview && (
        <div className={`fixed inset-0 ${isDark ? 'bg-black/70' : 'bg-black/50'} flex items-center justify-center z-50`} onClick={() => setShowHtmlPreview(false)}>
          <div className={`${isDark ? 'bg-[#1A1A1A] border-white/20' : 'bg-white border-gray-200'} border rounded-sm p-6 w-full max-w-4xl max-h-[80vh] overflow-hidden flex flex-col`} onClick={e => e.stopPropagation()}>
            <h3 className={`${isDark ? 'text-white' : 'text-gray-900'} font-bold mb-4 flex items-center gap-2`}>
              <FileCode className={`w-5 h-5 ${isDark ? 'text-[#A2FF00]' : 'text-green-500'}`} />
              HTML Template Preview
            </h3>
            <p className={`${isDark ? 'text-gray-400' : 'text-gray-600'} text-sm mb-4`}>
              Preview your HTML template before inserting it into the editor. This is great for presentation slides or custom layouts.
            </p>
            <div className="flex-1 overflow-auto bg-white rounded-sm p-4 mb-4 border border-gray-200">
              <div dangerouslySetInnerHTML={{ __html: sanitizeHTML(htmlPreviewContent) }} />
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => { setShowHtmlPreview(false); setHtmlPreviewContent(''); }}
                className={`px-4 py-2 ${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'} transition-colors`}
              >
                Cancel
              </button>
              <button
                onClick={insertHtmlContent}
                className={`px-4 py-2 ${isDark ? 'bg-[#A2FF00] hover:bg-[#8FE600]' : 'bg-green-500 hover:bg-green-600'} text-black font-bold rounded-sm transition-colors`}
              >
                Insert HTML
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Editor Content */}
      <EditorContent editor={editor} className={isDark ? 'editor-dark' : 'editor-light'} />

      {/* Editor Styles */}
      <style>{`
        .ProseMirror {
          min-height: 300px;
          padding: 1rem;
          outline: none;
        }
        .editor-dark .ProseMirror p {
          margin: 0.5rem 0;
          color: #d1d5db;
        }
        .editor-light .ProseMirror p {
          margin: 0.5rem 0;
          color: #374151;
        }
        .editor-dark .ProseMirror h1 {
          font-size: 2rem;
          font-weight: bold;
          color: #A2FF00;
          margin: 1.5rem 0 1rem;
          font-family: 'Unbounded', sans-serif;
        }
        .editor-light .ProseMirror h1 {
          font-size: 2rem;
          font-weight: bold;
          color: #16a34a;
          margin: 1.5rem 0 1rem;
          font-family: 'Unbounded', sans-serif;
        }
        .editor-dark .ProseMirror h2 {
          font-size: 1.5rem;
          font-weight: bold;
          color: #A2FF00;
          margin: 1.25rem 0 0.75rem;
          font-family: 'Unbounded', sans-serif;
        }
        .editor-light .ProseMirror h2 {
          font-size: 1.5rem;
          font-weight: bold;
          color: #16a34a;
          margin: 1.25rem 0 0.75rem;
          font-family: 'Unbounded', sans-serif;
        }
        .editor-dark .ProseMirror h3 {
          font-size: 1.25rem;
          font-weight: bold;
          color: white;
          margin: 1rem 0 0.5rem;
        }
        .editor-light .ProseMirror h3 {
          font-size: 1.25rem;
          font-weight: bold;
          color: #111827;
          margin: 1rem 0 0.5rem;
        }
        .editor-dark .ProseMirror ul, .editor-dark .ProseMirror ol {
          padding-left: 1.5rem;
          margin: 0.5rem 0;
          color: #d1d5db;
        }
        .editor-light .ProseMirror ul, .editor-light .ProseMirror ol {
          padding-left: 1.5rem;
          margin: 0.5rem 0;
          color: #374151;
        }
        .ProseMirror li {
          margin: 0.25rem 0;
        }
        .ProseMirror ul {
          list-style-type: disc;
        }
        .ProseMirror ol {
          list-style-type: decimal;
        }
        .ProseMirror ul[data-type="taskList"] {
          list-style: none;
          padding-left: 0;
        }
        .ProseMirror ul[data-type="taskList"] li {
          display: flex;
          align-items: flex-start;
          gap: 0.5rem;
        }
        .ProseMirror ul[data-type="taskList"] li > label {
          flex-shrink: 0;
          margin-top: 0.25rem;
        }
        .ProseMirror ul[data-type="taskList"] li > label input[type="checkbox"] {
          width: 1rem;
          height: 1rem;
          accent-color: #A2FF00;
        }
        .ProseMirror ul[data-type="taskList"] li[data-checked="true"] > div > p {
          text-decoration: line-through;
          color: #6b7280;
        }
        .editor-dark .ProseMirror blockquote {
          border-left: 3px solid #A2FF00;
          padding-left: 1rem;
          margin: 1rem 0;
          color: #9ca3af;
          font-style: italic;
        }
        .editor-light .ProseMirror blockquote {
          border-left: 3px solid #16a34a;
          padding-left: 1rem;
          margin: 1rem 0;
          color: #6b7280;
          font-style: italic;
        }
        .editor-dark .ProseMirror code {
          background: #1a1a1a;
          padding: 0.2rem 0.4rem;
          border-radius: 3px;
          font-family: monospace;
          color: #A2FF00;
        }
        .editor-light .ProseMirror code {
          background: #f3f4f6;
          padding: 0.2rem 0.4rem;
          border-radius: 3px;
          font-family: monospace;
          color: #16a34a;
        }
        .editor-dark .ProseMirror pre {
          background: #1a1a1a;
          padding: 1rem;
          border-radius: 4px;
          overflow-x: auto;
          margin: 1rem 0;
        }
        .editor-light .ProseMirror pre {
          background: #f3f4f6;
          padding: 1rem;
          border-radius: 4px;
          overflow-x: auto;
          margin: 1rem 0;
        }
        .ProseMirror pre code {
          background: none;
          padding: 0;
        }
        .editor-dark .ProseMirror hr {
          border: none;
          border-top: 1px solid rgba(255,255,255,0.2);
          margin: 1.5rem 0;
        }
        .editor-light .ProseMirror hr {
          border: none;
          border-top: 1px solid rgba(0,0,0,0.1);
          margin: 1.5rem 0;
        }
        .editor-dark .ProseMirror a {
          color: #A2FF00;
          text-decoration: underline;
        }
        .editor-light .ProseMirror a {
          color: #16a34a;
          text-decoration: underline;
        }
        .ProseMirror img {
          max-width: 100%;
          height: auto;
          border-radius: 4px;
          margin: 1rem 0;
        }
        .ProseMirror table {
          border-collapse: collapse;
          width: 100%;
          margin: 1rem 0;
        }
        .editor-dark .ProseMirror th, .editor-dark .ProseMirror td {
          border: 1px solid rgba(255,255,255,0.2);
          padding: 0.5rem;
          text-align: left;
        }
        .editor-light .ProseMirror th, .editor-light .ProseMirror td {
          border: 1px solid rgba(0,0,0,0.1);
          padding: 0.5rem;
          text-align: left;
        }
        .editor-dark .ProseMirror th {
          background: #111;
          font-weight: bold;
          color: #A2FF00;
        }
        .editor-light .ProseMirror th {
          background: #f9fafb;
          font-weight: bold;
          color: #16a34a;
        }
        .editor-dark .ProseMirror td {
          color: #d1d5db;
        }
        .editor-light .ProseMirror td {
          color: #374151;
        }
        .ProseMirror .is-empty::before {
          content: attr(data-placeholder);
          color: #6b7280;
          pointer-events: none;
          float: left;
          height: 0;
        }
        .ProseMirror:focus {
          outline: none;
        }
        .ProseMirror strong {
          font-weight: bold;
        }
        .ProseMirror em {
          font-style: italic;
        }
        .ProseMirror u {
          text-decoration: underline;
        }
        .ProseMirror s {
          text-decoration: line-through;
        }
        .ProseMirror sub {
          vertical-align: sub;
          font-size: 0.75em;
        }
        .ProseMirror sup {
          vertical-align: super;
          font-size: 0.75em;
        }
        .ProseMirror mark {
          border-radius: 2px;
          padding: 0 2px;
        }
        .ProseMirror .column-layout {
          display: grid;
          gap: 1rem;
          margin: 1rem 0;
        }
        .ProseMirror .column {
          border: 1px dashed rgba(255,255,255,0.2);
          padding: 1rem;
          min-height: 100px;
          border-radius: 4px;
        }
        .ProseMirror iframe {
          width: 100%;
          aspect-ratio: 16/9;
          border-radius: 4px;
          margin: 1rem 0;
        }
        .ProseMirror .video-embed {
          position: relative;
          padding-bottom: 56.25%;
          height: 0;
          overflow: hidden;
          margin: 1rem 0;
          border-radius: 8px;
        }
        .ProseMirror .video-embed iframe {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          border: none;
        }
        .ProseMirror .html-template-block {
          margin: 1rem 0;
          padding: 1rem;
          border: 1px solid rgba(255,255,255,0.1);
          border-radius: 8px;
          background: rgba(0,0,0,0.3);
        }
        .ProseMirror .html-template-block img {
          max-width: 100%;
          height: auto;
        }
        .ProseMirror .selectedCell {
          background: rgba(162, 255, 0, 0.1);
        }
        .ProseMirror .tableWrapper {
          overflow-x: auto;
        }
        .ProseMirror .resize-cursor {
          cursor: col-resize;
        }
      `}</style>
    </div>
  );
};

export default RichTextEditor;
